import { useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { type TFunction } from "i18next";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { ApiError, CREDENTIAL_ACTIVITY_SCOPES } from "@njbsoft/api-client";
import { downscaleImage } from "@njbsoft/form-renderer";
import { BREAKPOINT_SM_QUERY, useMediaQuery } from "@njbsoft/form-ui";
import { type components } from "@njbsoft/types/field";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  EmptyValue,
  PageHeader,
  Skeleton,
  SkeletonTable,
  type BadgeVariant,
} from "@/components/ui";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { CredentialFormDialog } from "@/components/tester/credentials/credential-form-dialog";
import {
  credentialDaysUntil,
  credentialExpiryVariant,
} from "@/components/tester/credentials/credential-expiry";
import { myCredentialEvidenceQuery } from "@/hooks/use-my-credential-evidence";
import { useUploadMyCredentialEvidence } from "@/hooks/use-my-credential-mutations";
import { useMyFieldCredentials } from "@/hooks/use-my-field-credentials";
import { useIsOnline } from "@/hooks/use-is-online";
import { useToast } from "@/hooks/use-toast";
import { companyKeys } from "@/lib/query-keys";
import { formatDateOnly } from "@/lib/dates";

type FieldCredential = components["schemas"]["FieldCredential"];
type CredentialActivityScope = components["schemas"]["CredentialActivityScope"];

export const Route = createFileRoute("/_company/credentials/")({
  component: TesterMyCredentialsPage,
});

const columnHelper = createColumnHelper<FieldCredential>();

/** Credential, Number, Issued, Expires, Evidence, Actions. */
export const CREDENTIALS_COLUMN_COUNT = 6;

/**
 * Split the credential list into one group per activity scope PRESENT, in the
 * runtime value set's order. A scope with no rows renders no card at all — an
 * empty group heading would name a certification the tester does not hold.
 * Server order is preserved within each group at first render; the tables
 * sort client-side over the whole (short, fully fetched) list.
 */
export function buildCredentialScopeGroups(
  credentials: FieldCredential[],
): { scope: CredentialActivityScope; credentials: FieldCredential[] }[] {
  return CREDENTIAL_ACTIVITY_SCOPES.flatMap((scope) => {
    const inScope = credentials.filter((c) => c.activity_scope === scope);
    return inScope.length > 0 ? [{ scope, credentials: inScope }] : [];
  });
}

/**
 * The banner's predicate: every credential whose expiry is still ahead but
 * falls inside the strict 60-day warning window — the warning-variant rows
 * and nothing else. Already-expired rows are NOT banner material: the table's
 * danger badge is the honest reading of a window already closed. Soonest
 * expiry first, so the banner's Update action targets the most urgent one.
 */
export function expiringCredentials(
  credentials: FieldCredential[],
  now: Date = new Date(),
): FieldCredential[] {
  return credentials
    .filter((c) => credentialExpiryVariant(c.expires_at, now) === "warning")
    .sort((a, b) => a.expires_at.localeCompare(b.expires_at));
}

/**
 * The Expires column's badge: three variants, three labels — danger renders
 * the expired line, warning interpolates the days left, and the far-out
 * resting state renders the dated line on a SUCCESS badge (a credential with
 * runway is the good state, unlike the flat list's neutral default).
 */
export function credentialExpiryBadge(
  t: TFunction,
  credential: FieldCredential,
  now: Date = new Date(),
): { variant: BadgeVariant; label: string } {
  const variant = credentialExpiryVariant(credential.expires_at, now);
  if (variant === "danger") {
    return { variant: "danger", label: t("field-work:credentials.expired") };
  }
  if (variant === "warning") {
    return {
      variant: "warning",
      label: t("field-work:credentials.expiresWithDays", {
        date: formatDateOnly(credential.expires_at),
        days: credentialDaysUntil(credential.expires_at, now),
      }),
    };
  }
  return {
    variant: "success",
    label: t("field-work:credentials.expires", { date: formatDateOnly(credential.expires_at) }),
  };
}

interface CredentialTableActions {
  onEdit: (credential: FieldCredential) => void;
}

export interface CredentialColumnOptions {
  /**
   * Lead with the action column instead of trailing with it: below `sm` the
   * table scrolls inside its own box, and trailing verbs would sit past the
   * phone's edge. Every other column keeps its order and its sort declaration.
   */
  actionFirst?: boolean;
}

/**
 * The scope card's table columns. Every data column declares
 * `enableSorting: true` — the list is client-sorted (the field read paginates
 * nothing), so each header reorders the whole set — and the action column
 * opts out on the spot. Evidence sorts on the latest certificate's file name
 * (never-used trails via sortUndefined), which is exactly what the chip
 * renders — sorting anything else would reorder nothing the eye can check.
 */
export function buildCredentialColumns(
  t: TFunction,
  nowIso: string,
  latestEvidenceName: Record<string, string | null>,
  actions: CredentialTableActions,
  { actionFirst = false }: CredentialColumnOptions = {},
) {
  const now = new Date(nowIso);
  const data = [
    columnHelper.accessor("certifying_body", {
      id: "credential",
      header: t("field-work:credentials.columns.credential"),
      enableSorting: true,
      cell: (ctx) => <span className="font-medium">{ctx.getValue()}</span>,
    }),
    columnHelper.accessor("identification_number", {
      id: "number",
      header: t("field-work:credentials.columns.number"),
      enableSorting: true,
      cell: (ctx) => <span className="text-muted">{ctx.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.issued_at ?? null, {
      id: "issued",
      header: t("field-work:credentials.columns.issued"),
      enableSorting: true,
      sortUndefined: "last",
      cell: (ctx) => {
        const issued = ctx.getValue();
        return issued ? formatDateOnly(issued) : <EmptyValue />;
      },
    }),
    columnHelper.accessor("expires_at", {
      id: "expires",
      header: t("field-work:credentials.columns.expires"),
      enableSorting: true,
      cell: ({ row }) => {
        const badge = credentialExpiryBadge(t, row.original, now);
        return <Badge variant={badge.variant}>{badge.label}</Badge>;
      },
    }),
    columnHelper.accessor((row) => latestEvidenceName[row.id] ?? null, {
      id: "evidence",
      header: t("field-work:credentials.columns.evidence"),
      enableSorting: true,
      sortUndefined: "last",
      cell: (ctx) => {
        const fileName = ctx.getValue();
        return fileName ? <EvidenceChip fileName={fileName} /> : <EmptyValue />;
      },
    }),
  ];
  const actionColumn = columnHelper.display({
    id: "actions",
    header: t("field-work:credentials.columns.actions"),
    enableSorting: false,
    cell: ({ row }) => (
      // The row's own click opens the credential's detail route, so the
      // action cell stops the click from bubbling — the sibling row-action
      // pattern (forms/templates-columns): a row's verb must never both
      // fire AND navigate.
      <div className="flex justify-end gap-2" onClick={(event) => event.stopPropagation()}>
        <Button
          size="sm"
          variant="secondary"
          className="min-h-11 min-w-11"
          onClick={() => {
            actions.onEdit(row.original);
          }}
        >
          {t("common:edit")}
        </Button>
        <ReplacePhotoButton credential={row.original} />
      </div>
    ),
  });
  return actionFirst ? [actionColumn, ...data] : [...data, actionColumn];
}

/**
 * The evidence chip: the newest certificate's file name with an on-file note
 * — the one-glance answer to "did the photo make it up". The chip renders
 * from the page's resolved evidence map; a row with no chip is a row with no
 * certificate, and the detail route is where its full life is managed.
 */
function EvidenceChip({ fileName }: { fileName: string }) {
  const { t } = useTranslation(["field-work", "common"]);
  return (
    <span className="inline-flex max-w-56 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs dark:bg-gray-700">
      <span aria-hidden="true" className="font-medium text-primary">
        ✓
      </span>
      <span className="truncate font-medium text-on-surface">{fileName}</span>
      <span className="flex-shrink-0 text-muted">{t("field-work:credentials.onFile")}</span>
    </span>
  );
}

/** Swaps the original extension for `.jpg`, for a re-encode that changed the container. */
function jpegFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem}.jpg`;
}

/**
 * Replace photo: the one-tap re-certification capture. The edit form is the
 * full-edit affordance; this is the row verb for the common case — the
 * credential is fine, the PHOTO is what the utility needs refreshed. The
 * capture walks the same seam the form's second step and the detail route's
 * upload control use (downscale, container-honest filename, the evidence
 * upload hook), and it is online-only like every credential write: record
 * upkeep is not field capture.
 */
function ReplacePhotoButton({ credential }: { credential: FieldCredential }) {
  const { t } = useTranslation(["field-work", "common"]);
  const toast = useToast();
  const online = useIsOnline();
  const queryClient = useQueryClient();
  const upload = useUploadMyCredentialEvidence();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCapture = async (file: File) => {
    const bounded = await downscaleImage(file);
    const fileName = bounded.type === file.type ? file.name : jpegFileName(file.name);
    upload.mutate(
      { credentialId: credential.id, file: { blob: bounded, fileName } },
      {
        onSuccess: () => {
          toast.success("field-work:credentials.evidenceUploaded");
          // The upload hook invalidates the credential LIST; the certificate
          // the tester just replaced rides the evidence query, which nothing
          // else refreshes — the chip would show yesterday's photo for the
          // staleTime otherwise.
          void queryClient.invalidateQueries({
            queryKey: companyKeys.fieldCredentialEvidence(credential.id),
          });
        },
        onError: (err) => {
          toast.apiError(err instanceof ApiError ? err.code : undefined);
        },
      },
    );
  };

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        className="min-h-11 min-w-11"
        disabled={!online}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        {t("field-work:credentials.replacePhoto")}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        aria-label={t("field-work:credentials.replacePhoto")}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleCapture(file);
          event.target.value = "";
        }}
      />
    </>
  );
}

/**
 * My Credentials — the caller's own certification rows, grouped one card per
 * activity scope present (a person certifies into a scope, and the scope is
 * what a utility approves), each card a table of that scope's rows with the
 * expiry badge and the on-file evidence chip per row. `CREDENTIAL_ACTIVITY_SCOPES`
 * is the runtime value set's single home; the group order IS the array order.
 *
 * An expiring-soon banner sits above the cards: every credential inside the
 * strict 60-day window, soonest first, with an Update action opening the edit
 * form on the most urgent one — renew and re-photograph before it lapses.
 *
 * There is NO offline gate here, unlike My Kits: reading which certification
 * expires when is worth doing from a cached list, and the gate that matters —
 * writes need a connection — lives in the form and in the replace-photo
 * control. A credential is personal, so every row on this page is the
 * caller's own and every row carries the verbs.
 */
function TesterMyCredentialsPage() {
  const { t } = useTranslation(["field-work", "common"]);
  const navigate = useNavigate();

  const query = useMyFieldCredentials();
  const credentials = query.data?.items ?? [];

  // One evidence read per credential (the wire carries no evidence summary):
  // the newest file name feeds both the Evidence chip and its sort key.
  const evidenceQueries = useQueries({
    queries: credentials.map((credential) => myCredentialEvidenceQuery(credential.id)),
  });
  const latestEvidenceName: Record<string, string | null> = {};
  credentials.forEach((credential, index) => {
    const items = evidenceQueries[index]?.data?.items ?? [];
    // Newest first is the evidence list's server order; the chip shows the
    // certificate a renewal would replace.
    latestEvidenceName[credential.id] = items[0]?.file_name ?? null;
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FieldCredential | null>(null);
  const fromSm = useMediaQuery(BREAKPOINT_SM_QUERY, true);

  const handleEdit = (credential: FieldCredential) => {
    setEditing(credential);
    setFormOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
  };

  if (query.error instanceof ApiError && query.error.status === 403) {
    return <FieldAccessEnded />;
  }

  if (query.isLoading) {
    return (
      <div className="space-y-6 p-4 sm:p-8">
        <Skeleton width="240px" height="36px" />
        <SkeletonTable columns={CREDENTIALS_COLUMN_COUNT} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex min-h-64 items-center justify-center p-4 sm:p-8">
        <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
      </div>
    );
  }

  const now = new Date();
  const expiring = expiringCredentials(credentials, now);
  const groups = buildCredentialScopeGroups(credentials);
  const columns = buildCredentialColumns(
    t,
    now.toISOString(),
    latestEvidenceName,
    { onEdit: handleEdit },
    { actionFirst: !fromSm },
  );

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t("field-work:credentials.title")}
        subtitle={t("field-work:credentials.subtitle")}
        actions={<Button onClick={handleAdd}>{t("field-work:credentials.add")}</Button>}
      />

      {expiring.length > 0 && (
        // A window still open, not an alarm already sounded: the tables'
        // danger badges carry the expired rows, the banner carries the ones a
        // renewal today still saves.
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-100 p-4 dark:bg-amber-900"
        >
          <p className="min-w-0 flex-1 text-sm text-amber-800 dark:text-amber-300">
            {t("field-work:credentials.expiringBanner.body", {
              count: expiring.length,
              credentials: expiring
                .map(
                  (credential) =>
                    `${credential.certifying_body} (${formatDateOnly(credential.expires_at)})`,
                )
                .join(", "),
            })}
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              handleEdit(expiring[0] as FieldCredential);
            }}
          >
            {t("field-work:credentials.update")}
          </Button>
        </div>
      )}

      {credentials.length === 0 ? (
        // Title only, no description — unlike My Kits. There is nothing to
        // explain: a tester with no credential on file knows it.
        <EmptyState title={t("field-work:credentials.empty")} />
      ) : (
        groups.map(({ scope, credentials: inScope }) => (
          <section key={scope} className="space-y-2">
            <h2 className="text-lg font-semibold text-on-surface">
              {t(`field-work:credentials.scopeLabels.${scope}`)}
            </h2>
            <p className="text-sm text-muted">
              {t(`field-work:credentials.scopeDescriptions.${scope}`)}
            </p>
            <DataTable
              columns={columns}
              data={inScope}
              manualSorting={false}
              onRowClick={(credential) => {
                void navigate({
                  to: "/credentials/$credentialId",
                  params: { credentialId: credential.id },
                });
              }}
            />
          </section>
        ))
      )}
      <CredentialFormDialog
        open={formOpen}
        credential={editing}
        onClose={closeForm}
        onSaved={closeForm}
      />
    </div>
  );
}
